import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { FiSearch, FiFilter, FiAward } from 'react-icons/fi';
import { useApi } from '../hooks/useApi';
import MatchCard, { getSideLabel, formatQuota } from '../components/MatchCard/MatchCard';
import Modal from '../components/Modal/Modal';
import Pagination from '../components/Pagination/Pagination';
import WinningMarketsModal from '../components/WinningMarketsModal/WinningMarketsModal';
import p from './Pages.module.css';

const LAMBDA_KPI_FIELDS = [
  { key: 'lambdaHome', label: 'λ Home' }, { key: 'lambdaAway', label: 'λ Away' }, { key: 'lambdaTotal', label: 'λ Total' },
  { key: 'lambdaHomeHt', label: 'λ H HT' }, { key: 'lambdaAwayHt', label: 'λ A HT' },
  { key: 'lambdaHomeSh', label: 'λ H 2H' }, { key: 'lambdaAwaySh', label: 'λ A 2H' },
  { key: 'lambdaHomeCorners', label: 'λ H Corners' }, { key: 'lambdaAwayCorners', label: 'λ A Corners' },
  { key: 'lambdaHomeHtCorners', label: 'λ H HT C' }, { key: 'lambdaAwayHtCorners', label: 'λ A HT C' },
  { key: 'lambdaHomeYc', label: 'λ H YC' }, { key: 'lambdaAwayYc', label: 'λ A YC' },
  { key: 'lambdaHomeHtYc', label: 'λ H HT YC' }, { key: 'lambdaAwayHtYc', label: 'λ A HT YC' },
  { key: 'lambdaHomeSot', label: 'λ H SOT' }, { key: 'lambdaAwaySot', label: 'λ A SOT' },
  { key: 'lambdaHomeHtSot', label: 'λ H HT SOT' }, { key: 'lambdaAwayHtSot', label: 'λ A HT SOT' },
];

function secsToTimeStr(secs) {
  if (secs == null || secs === '') return '';
  if (typeof secs === 'string' && /^\d+:\d{1,2}(\.\d+)?$/.test(secs)) return secs;
  const s = Number(secs);
  if (isNaN(s)) return '';
  const m = Math.floor(s / 60); const sec = s % 60;
  const w = Math.floor(sec); const ms = Math.round((sec - w) * 1000);
  return `${String(m).padStart(2,'0')}:${String(w).padStart(2,'0')}.${String(ms).padStart(3,'0')}`;
}

const QUOTA_SECTIONS = [
  { label: 'FT — 1x2', fields: [{ key: 'rh', l: 'Home' },{ key: 'rd', l: 'Draw' },{ key: 'ra', l: 'Away' }] },
  { label: 'FT — DC', fields: [{ key: 'dc1x', l: '1X' },{ key: 'dc12', l: '12' },{ key: 'dcx2', l: 'X2' }] },
  { label: 'First Goal', fields: [{ key: 'fgHome', l: 'Home' },{ key: 'fgNogoal', l: 'No Goal' },{ key: 'fgAway', l: 'Away' }] },
  { label: 'FT — O/U 2.5', fields: [{ key: 'ou25o', l: 'Over' },{ key: 'ou25u', l: 'Under' }] },
  { label: 'FT — O/U 3.5', fields: [{ key: 'ou35o', l: 'Over' },{ key: 'ou35u', l: 'Under' }] },
  { label: 'FT — O/U 4.5', fields: [{ key: 'ou45o', l: 'Over' },{ key: 'ou45u', l: 'Under' }] },
  { label: 'FT — O/U 5.5', fields: [{ key: 'ou55o', l: 'Over' },{ key: 'ou55u', l: 'Under' }] },
  { label: 'FT — O/U 6.5', fields: [{ key: 'ou65o', l: 'Over' },{ key: 'ou65u', l: 'Under' }] },
  { label: 'FT — EH', fields: [{ key: 'eh10r1', l: 'EH1:0-1' },{ key: 'eh10rx', l: 'EH1:0-X' },{ key: 'eh10r2', l: 'EH1:0-2' },{ key: 'eh01r1', l: 'EH0:1-1' },{ key: 'eh01rx', l: 'EH0:1-X' },{ key: 'eh01r2', l: 'EH0:1-2' }] },
  { label: 'HT — 1x2', fields: [{ key: 'rhth', l: 'Home' },{ key: 'rhtd', l: 'Draw' },{ key: 'rhta', l: 'Away' }] },
  { label: 'HT — DC', fields: [{ key: 'htDc1x', l: '1X' },{ key: 'htDc12', l: '12' },{ key: 'htDcx2', l: 'X2' }] },
  { label: 'HT — O/U 0.5', fields: [{ key: 'htou05o', l: 'Over' },{ key: 'htou05u', l: 'Under' }] },
  { label: 'HT — O/U 1.5', fields: [{ key: 'htou15o', l: 'Over' },{ key: 'htou15u', l: 'Under' }] },
  { label: 'HT — O/U 2.5', fields: [{ key: 'htou25o', l: 'Over' },{ key: 'htou25u', l: 'Under' }] },
  { label: 'HT — EH', fields: [{ key: 'htEh10r1', l: 'EH1:0-1' },{ key: 'htEh10rx', l: 'EH1:0-X' },{ key: 'htEh10r2', l: 'EH1:0-2' },{ key: 'htEh01r1', l: 'EH0:1-1' },{ key: 'htEh01rx', l: 'EH0:1-X' },{ key: 'htEh01r2', l: 'EH0:1-2' }] },
  { label: 'HT — BTTS', fields: [{ key: 'htBttsYes', l: 'Yes' },{ key: 'htBttsNo', l: 'No' }] },
  // 2nd Half Goals
  { label: '2H — 1x2', fields: [{ key: 'shR1', l: 'Home' },{ key: 'shRx', l: 'Draw' },{ key: 'shR2', l: 'Away' }] },
  { label: '2H — DC', fields: [{ key: 'shDc1x', l: '1X' },{ key: 'shDc12', l: '12' },{ key: 'shDcx2', l: 'X2' }] },
  { label: '2H — O/U 0.5', fields: [{ key: 'shOu05o', l: 'Over' },{ key: 'shOu05u', l: 'Under' }] },
  { label: '2H — O/U 1.5', fields: [{ key: 'shOu15o', l: 'Over' },{ key: 'shOu15u', l: 'Under' }] },
  { label: '2H — O/U 2.5', fields: [{ key: 'shOu25o', l: 'Over' },{ key: 'shOu25u', l: 'Under' }] },
  { label: '2H — EH', fields: [{ key: 'shEh10r1', l: 'EH1:0-1' },{ key: 'shEh10rx', l: 'EH1:0-X' },{ key: 'shEh10r2', l: 'EH1:0-2' },{ key: 'shEh01r1', l: 'EH0:1-1' },{ key: 'shEh01rx', l: 'EH0:1-X' },{ key: 'shEh01r2', l: 'EH0:1-2' }] },
  { label: '2H — BTTS', fields: [{ key: 'shBttsYes', l: 'Yes' },{ key: 'shBttsNo', l: 'No' }] },
  // Special FT
  { label: 'Highest Scoring Half', fields: [{ key: 'hshFirst', l: 'First' },{ key: 'hshTie', l: 'Tie' },{ key: 'hshSecond', l: 'Second' }] },
  { label: 'Win to Nil', fields: [{ key: 'wtnHome', l: 'Home' },{ key: 'wtnAway', l: 'Away' }] },
  { label: 'Draw No Bet', fields: [{ key: 'dnbHome', l: 'Home' },{ key: 'dnbAway', l: 'Away' }] },
  // Corners FT
  { label: 'Corners FT — 1x2', fields: [{ key: 'cornersR1', l: 'Home' },{ key: 'cornersRx', l: 'Draw' },{ key: 'cornersR2', l: 'Away' }] },
  { label: 'Corners FT — DC', fields: [{ key: 'cornersDc1x', l: '1X' },{ key: 'cornersDc12', l: '12' },{ key: 'cornersDcx2', l: 'X2' }] },
  { label: 'Corners FT — EH', fields: [{ key: 'cornersEh10r1', l: 'EH1:0-1' },{ key: 'cornersEh10rx', l: 'EH1:0-X' },{ key: 'cornersEh10r2', l: 'EH1:0-2' },{ key: 'cornersEh01r1', l: 'EH0:1-1' },{ key: 'cornersEh01rx', l: 'EH0:1-X' },{ key: 'cornersEh01r2', l: 'EH0:1-2' }] },
  { label: 'Corners FT — ET', fields: [{ key: 'cornersEt3Yes', l: '3+ Yes' },{ key: 'cornersEt3No', l: '3+ No' },{ key: 'cornersEt4Yes', l: '4+ Yes' },{ key: 'cornersEt4No', l: '4+ No' },{ key: 'cornersEt5Yes', l: '5+ Yes' },{ key: 'cornersEt5No', l: '5+ No' }] },
  // Corners 1H
  { label: 'Corners 1H — 1x2', fields: [{ key: 'htCornersR1', l: 'Home' },{ key: 'htCornersRx', l: 'Draw' },{ key: 'htCornersR2', l: 'Away' }] },
  { label: 'Corners 1H — DC', fields: [{ key: 'htCornersDc1x', l: '1X' },{ key: 'htCornersDc12', l: '12' },{ key: 'htCornersDcx2', l: 'X2' }] },
  { label: 'Corners 1H — EH', fields: [{ key: 'htCornersEh10r1', l: 'EH1:0-1' },{ key: 'htCornersEh10rx', l: 'EH1:0-X' },{ key: 'htCornersEh10r2', l: 'EH1:0-2' },{ key: 'htCornersEh01r1', l: 'EH0:1-1' },{ key: 'htCornersEh01rx', l: 'EH0:1-X' },{ key: 'htCornersEh01r2', l: 'EH0:1-2' }] },
  { label: 'Corners 1H — BT', fields: [{ key: 'htCornersBt1Yes', l: '1+ Yes' },{ key: 'htCornersBt1No', l: '1+ No' },{ key: 'htCornersBt2Yes', l: '2+ Yes' },{ key: 'htCornersBt2No', l: '2+ No' },{ key: 'htCornersBt3Yes', l: '3+ Yes' },{ key: 'htCornersBt3No', l: '3+ No' }] },
  // Yellow Cards FT
  { label: 'YC FT — 1x2', fields: [{ key: 'ycR1', l: 'Home' },{ key: 'ycRx', l: 'Draw' },{ key: 'ycR2', l: 'Away' }] },
  { label: 'YC FT — DC', fields: [{ key: 'ycDc1x', l: '1X' },{ key: 'ycDc12', l: '12' },{ key: 'ycDcx2', l: 'X2' }] },
  { label: 'YC FT — EH', fields: [{ key: 'ycEh10r1', l: 'EH1:0-1' },{ key: 'ycEh10rx', l: 'EH1:0-X' },{ key: 'ycEh10r2', l: 'EH1:0-2' },{ key: 'ycEh01r1', l: 'EH0:1-1' },{ key: 'ycEh01rx', l: 'EH0:1-X' },{ key: 'ycEh01r2', l: 'EH0:1-2' }] },
  { label: 'YC FT — ET', fields: [{ key: 'ycEt1Yes', l: '1+ Yes' },{ key: 'ycEt1No', l: '1+ No' },{ key: 'ycEt2Yes', l: '2+ Yes' },{ key: 'ycEt2No', l: '2+ No' },{ key: 'ycEt3Yes', l: '3+ Yes' },{ key: 'ycEt3No', l: '3+ No' }] },
  // Yellow Cards 1H
  { label: 'YC 1H — 1x2', fields: [{ key: 'htYcR1', l: 'Home' },{ key: 'htYcRx', l: 'Draw' },{ key: 'htYcR2', l: 'Away' }] },
  { label: 'YC 1H — DC', fields: [{ key: 'htYcDc1x', l: '1X' },{ key: 'htYcDc12', l: '12' },{ key: 'htYcDcx2', l: 'X2' }] },
  { label: 'YC 1H — EH', fields: [{ key: 'htYcEh10r1', l: 'EH1:0-1' },{ key: 'htYcEh10rx', l: 'EH1:0-X' },{ key: 'htYcEh10r2', l: 'EH1:0-2' },{ key: 'htYcEh01r1', l: 'EH0:1-1' },{ key: 'htYcEh01rx', l: 'EH0:1-X' },{ key: 'htYcEh01r2', l: 'EH0:1-2' }] },
  { label: 'YC 1H — ET', fields: [{ key: 'htYcEt1Yes', l: '1+ Yes' },{ key: 'htYcEt1No', l: '1+ No' }] },
  // SOT FT
  { label: 'SOT FT — 1x2', fields: [{ key: 'sotR1', l: 'Home' },{ key: 'sotRx', l: 'Draw' },{ key: 'sotR2', l: 'Away' }] },
  { label: 'SOT FT — DC', fields: [{ key: 'sotDc1x', l: '1X' },{ key: 'sotDc12', l: '12' },{ key: 'sotDcx2', l: 'X2' }] },
  { label: 'SOT FT — EH', fields: [{ key: 'sotEh10r1', l: 'EH1:0-1' },{ key: 'sotEh10rx', l: 'EH1:0-X' },{ key: 'sotEh10r2', l: 'EH1:0-2' },{ key: 'sotEh01r1', l: 'EH0:1-1' },{ key: 'sotEh01rx', l: 'EH0:1-X' },{ key: 'sotEh01r2', l: 'EH0:1-2' }] },
  { label: 'SOT FT — ET', fields: [{ key: 'sotEt4Yes', l: '4+ Yes' },{ key: 'sotEt4No', l: '4+ No' },{ key: 'sotEt5Yes', l: '5+ Yes' },{ key: 'sotEt5No', l: '5+ No' },{ key: 'sotEt6Yes', l: '6+ Yes' },{ key: 'sotEt6No', l: '6+ No' },{ key: 'sotEt7Yes', l: '7+ Yes' },{ key: 'sotEt7No', l: '7+ No' }] },
  // SOT 1H
  { label: 'SOT 1H — 1x2', fields: [{ key: 'htSotR1', l: 'Home' },{ key: 'htSotRx', l: 'Draw' },{ key: 'htSotR2', l: 'Away' }] },
  { label: 'SOT 1H — DC', fields: [{ key: 'htSotDc1x', l: '1X' },{ key: 'htSotDc12', l: '12' },{ key: 'htSotDcx2', l: 'X2' }] },
  { label: 'SOT 1H — EH', fields: [{ key: 'htSotEh10r1', l: 'EH1:0-1' },{ key: 'htSotEh10rx', l: 'EH1:0-X' },{ key: 'htSotEh10r2', l: 'EH1:0-2' },{ key: 'htSotEh01r1', l: 'EH0:1-1' },{ key: 'htSotEh01rx', l: 'EH0:1-X' },{ key: 'htSotEh01r2', l: 'EH0:1-2' }] },
  { label: 'SOT 1H — BT', fields: [{ key: 'htSotBt1Yes', l: '1+ Yes' },{ key: 'htSotBt1No', l: '1+ No' },{ key: 'htSotBt2Yes', l: '2+ Yes' },{ key: 'htSotBt2No', l: '2+ No' },{ key: 'htSotBt3Yes', l: '3+ Yes' },{ key: 'htSotBt3No', l: '3+ No' }] },
];

// Build dynamic O/U sections from quota for display
function buildDynamicOuSections(quotaObj) {
  if (!quotaObj) return [];
  const sections = [];
  const cats = [
    { prefix: 'cornersOu', label: 'Corners FT', count: 3 },
    { prefix: 'htCornersOu', label: 'Corners 1H', count: 3 },
    { prefix: 'ycOu', label: 'YC FT', count: 3 },
    { prefix: 'htYcOu', label: 'YC 1H', count: 3 },
    { prefix: 'sotOu', label: 'SOT FT', count: 3 },
    { prefix: 'htSotOu', label: 'SOT 1H', count: 3 },
  ];
  for (const { prefix, label, count } of cats) {
    for (let n = 1; n <= count; n++) {
      const lineKey = `${prefix}${n}Line`;
      const lineVal = quotaObj[lineKey];
      if (lineVal == null) continue;
      sections.push({
        label: `${label} — O/U ${lineVal}`,
        fields: [
          { key: `${prefix}${n}o`, l: 'Over' },
          { key: `${prefix}${n}u`, l: 'Under' },
        ],
      });
    }
  }
  return sections;
}

const TT_FIELDS = [
  { key: 'ttft15o', l: 'FT TT 1.5 O' },{ key: 'ttft15u', l: 'FT TT 1.5 U' },
  { key: 'ttht05o', l: 'HT TT 0.5 O' },{ key: 'ttht05u', l: 'HT TT 0.5 U' },
  { key: 'ttsh05o', l: '2H TT 0.5 O' },{ key: 'ttsh05u', l: '2H TT 0.5 U' },
];

const STAT_TT_FIELDS = [
  { lineKey: 'cornersTtLine', oKey: 'cornersTtO', uKey: 'cornersTtU', l: 'Corners TT FT' },
  { lineKey: 'htCornersTtLine', oKey: 'htCornersTtO', uKey: 'htCornersTtU', l: 'Corners TT 1H' },
  { lineKey: 'ycTtLine', oKey: 'ycTtO', uKey: 'ycTtU', l: 'YC TT FT' },
  { lineKey: 'htYcTtLine', oKey: 'htYcTtO', uKey: 'htYcTtU', l: 'YC TT 1H' },
  { lineKey: 'sotTtLine', oKey: 'sotTtO', uKey: 'sotTtU', l: 'SOT TT FT' },
  { lineKey: 'htSotTtLine', oKey: 'htSotTtO', uKey: 'htSotTtU', l: 'SOT TT 1H' },
];

function computeWinners(home, away) {
  const w = new Set();
  const hR = home?.result || {}; const aR = away?.result || {};
  const hFT = hR.fullTimeGoals; const aFT = aR.fullTimeGoals;
  const hFH = hR.firstHalfGoals; const aFH = aR.firstHalfGoals;
  const hasFT = hFT != null && aFT != null; const hasHT = hFH != null && aFH != null;
  if (hasFT) {
    const tFT = hFT + aFT;
    if (hFT > aFT) w.add('rh'); else if (hFT === aFT) w.add('rd'); else w.add('ra');
    if (hFT >= aFT) w.add('dc1x'); if (hFT !== aFT) w.add('dc12'); if (aFT >= hFT) w.add('dcx2');
    [2.5,3.5,4.5,5.5,6.5].forEach(v => { const k = v.toString().replace('.',''); if (tFT > v) w.add(`ou${k}o`); else w.add(`ou${k}u`); });
    const e1h = hFT+1; if (e1h > aFT) w.add('eh10r1'); else if (e1h === aFT) w.add('eh10rx'); else w.add('eh10r2');
    const e0a = aFT+1; if (hFT > e0a) w.add('eh01r1'); else if (hFT === e0a) w.add('eh01rx'); else w.add('eh01r2');
  }
  // Parse timeOfFirstGoal: could be a string "mm:ss.SSS" or a number (seconds)
  const parseGoalTime = (v) => {
    if (v == null) return null;
    if (typeof v === 'number') return v;
    const m = String(v).match(/^(\d+):(\d{1,2})(?:\.(\d{1,3}))?$/);
    if (m) return parseInt(m[1],10)*60 + parseInt(m[2],10) + (m[3] ? parseInt(m[3].padEnd(3,'0'),10)/1000 : 0);
    const n = Number(v); return isNaN(n) ? null : n;
  };
  const hMin = parseGoalTime(hR.timeOfFirstGoal) ?? hR.minuteOfFirstGoal; const aMin = parseGoalTime(aR.timeOfFirstGoal) ?? aR.minuteOfFirstGoal;
  if (hMin == null && aMin == null) w.add('fgNogoal'); else if (hMin != null && aMin != null) { if (hMin <= aMin) w.add('fgHome'); else w.add('fgAway'); } else if (hMin != null) w.add('fgHome'); else w.add('fgAway');
  if (hasHT) {
    const tHT = hFH + aFH;
    if (hFH > aFH) w.add('rhth'); else if (hFH === aFH) w.add('rhtd'); else w.add('rhta');
    if (hFH >= aFH) w.add('htDc1x'); if (hFH !== aFH) w.add('htDc12'); if (aFH >= hFH) w.add('htDcx2');
    [0.5,1.5,2.5].forEach(v => { const k = v.toString().replace('.',''); if (tHT > v) w.add(`htou${k}o`); else w.add(`htou${k}u`); });
    const he = hFH+1; if (he > aFH) w.add('htEh10r1'); else if (he === aFH) w.add('htEh10rx'); else w.add('htEh10r2');
    const ae = aFH+1; if (hFH > ae) w.add('htEh01r1'); else if (hFH === ae) w.add('htEh01rx'); else w.add('htEh01r2');
    if (hFH > 0 && aFH > 0) w.add('htBttsYes'); else w.add('htBttsNo');
  }
  return w;
}

export default function ClasicoResults() {
  const api = useApi();
  const PAGE_SIZE = 20;
  const [matches, setMatches] = useState([]); const [clasicos, setClasicos] = useState([]);
  const [matchSearch, setMatchSearch] = useState(''); const [clasicoSearch, setClasicoSearch] = useState('');
  const [selItem, setSelItem] = useState(null); const [selType, setSelType] = useState('match');
  const [winOpen, setWinOpen] = useState(false);
  const [mPage, setMPage] = useState(1); const [mPages, setMPages] = useState(1); const [mTotal, setMTotal] = useState(0);
  const [cPage, setCPage] = useState(1); const [cPages, setCPages] = useState(1); const [cTotal, setCTotal] = useState(0);
  const [showDateFilter, setShowDateFilter] = useState(false);
  const [dateFrom, setDateFrom] = useState(() => new Date().toISOString().slice(0, 10)); const [dateTo, setDateTo] = useState('');

  const buildQ = useCallback((page) => {
    let q = `?status=finished&page=${page}&size=${PAGE_SIZE}`;
    if (dateFrom) q += `&createdFrom=${dateFrom}T00:00:00`;
    if (dateTo) q += `&createdTo=${dateTo}T23:59:59`;
    return q;
  }, [dateFrom, dateTo]);

  const loadMatches = useCallback(async (page = 1) => {
    try { const r = await api.get(`/v1/match/${buildQ(page)}`); if (r?.items) { setMatches(r.items); setMPage(r.page); setMPages(r.pages); setMTotal(r.total); } else if (Array.isArray(r)) setMatches(r); } catch {}
  }, [api, buildQ]);

  const loadClasicos = useCallback(async (page = 1) => {
    try { const r = await api.get(`/v1/clasico/${buildQ(page)}`); if (r?.items) { setClasicos(r.items); setCPage(r.page); setCPages(r.pages); setCTotal(r.total); } else if (Array.isArray(r)) setClasicos(r); } catch {}
  }, [api, buildQ]);

  useEffect(() => {
    loadMatches(1); loadClasicos(1);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFrom, dateTo]);

  const filteredM = useMemo(() => { if (!matchSearch) return matches; const l = matchSearch.toLowerCase(); return matches.filter(m => m.teams?.some(t => t.name.toLowerCase().includes(l))); }, [matches, matchSearch]);
  const filteredC = useMemo(() => { if (!clasicoSearch) return clasicos; const l = clasicoSearch.toLowerCase(); return clasicos.filter(c => c.teams?.some(t => t.name.toLowerCase().includes(l))); }, [clasicos, clasicoSearch]);

  const openItem = async (item, type) => {
    try { const fresh = await api.get(`/v1/${type === 'clasico' ? 'clasico' : 'match'}/${item.id}`); setSelItem(fresh || item); } catch { setSelItem(item); }
    setSelType(type);
  };

  const home = selItem?.teams?.find(t => getSideLabel(t) === 'Home');
  const away = selItem?.teams?.find(t => getSideLabel(t) === 'Away');
  const wins = selItem ? computeWinners(home, away) : new Set();
  const q = useMemo(() => selItem?.quota || {}, [selItem]);
  const hFT = home?.result?.fullTimeGoals; const aFT = away?.result?.fullTimeGoals;
  const hFH = home?.result?.firstHalfGoals; const aFH = away?.result?.firstHalfGoals;
  const fmtInt = v => (v == null ? '—' : v);

  // Build dynamic O/U sections from selected item quota
  const dynamicOuSecs = useMemo(() => buildDynamicOuSections(q), [q]);

  // Per FRONTEND_MARKET_OPT_IN.md — stat-family sections are only meaningful on clasicos
  // when the corresponding lambda is non-null (otherwise the family was not calculated).
  const lam = selItem?.lambdas || {};
  const familyAllowed = useCallback((sectionLabel) => {
    if (selType !== 'clasico') return true;
    const checks = [
      { match: /^Corners FT/, key: 'lambdaHomeCorners' },
      { match: /^Corners 1H/, key: 'lambdaHomeHtCorners' },
      { match: /^YC FT/,      key: 'lambdaHomeYc' },
      { match: /^YC 1H/,      key: 'lambdaHomeHtYc' },
      { match: /^SOT FT/,     key: 'lambdaHomeSot' },
      { match: /^SOT 1H/,     key: 'lambdaHomeHtSot' },
    ];
    for (const c of checks) {
      if (c.match.test(sectionLabel)) return lam[c.key] != null;
    }
    return true;
  }, [selType, lam]);

  return (
    <div className={p.page}>
      <div className={p.pageHeader}><h1 className={p.pageTitle}>Clasico Results</h1><p className={p.pageSubtitle}>Finished matches and clasicos with winning quota highlights</p></div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <button onClick={() => setShowDateFilter(v => !v)} className={p.btnOutline} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.78rem', padding: '5px 12px' }}>
          <FiFilter size={13} /> {showDateFilter ? 'Hide Filters' : 'Date Filter'}
        </button>
        {showDateFilter && <>
          <input type="date" className={p.formInput} style={{ width: 150, fontSize: '0.78rem', padding: '5px 8px' }} value={dateFrom} onChange={e => { setDateFrom(e.target.value); setMPage(1); setCPage(1); }} />
          <span style={{ color: 'var(--text-muted)', fontSize: '0.78rem' }}>to</span>
          <input type="date" className={p.formInput} style={{ width: 150, fontSize: '0.78rem', padding: '5px 8px' }} value={dateTo} onChange={e => { setDateTo(e.target.value); setMPage(1); setCPage(1); }} />
          {(dateFrom || dateTo) && <button className={p.btnOutline} style={{ fontSize: '0.72rem', padding: '4px 10px' }} onClick={() => { setDateFrom(''); setDateTo(''); }}>Clear</button>}
        </>}
      </div>

      <div className={p.columns}>
        <div>
          <div className={p.colHeader}><h3 className={p.colTitle}>Matches</h3><div style={{ position: 'relative' }}><FiSearch style={{ position: 'absolute', left: 8, top: 8, color: 'var(--text-muted)', fontSize: '0.8rem' }} /><input className={p.searchInput} style={{ paddingLeft: 28, minWidth: 160 }} placeholder="Search…" value={matchSearch} onChange={e => setMatchSearch(e.target.value)} /></div></div>
          <div className={p.list}>{filteredM.length > 0 ? filteredM.map(m => <MatchCard key={m.id} match={m} onItemClick={(x) => openItem(x, 'match')} />) : <div className={p.emptyState}><div className={p.emptyText}>No finished matches</div></div>}</div>
          <Pagination page={mPage} pages={mPages} total={mTotal} size={PAGE_SIZE} onPageChange={pg => { setMPage(pg); loadMatches(pg); }} />
        </div>
        <div>
          <div className={p.colHeader}><h3 className={p.colTitle}>Clasicos</h3><div style={{ position: 'relative' }}><FiSearch style={{ position: 'absolute', left: 8, top: 8, color: 'var(--text-muted)', fontSize: '0.8rem' }} /><input className={p.searchInput} style={{ paddingLeft: 28, minWidth: 160 }} placeholder="Search…" value={clasicoSearch} onChange={e => setClasicoSearch(e.target.value)} /></div></div>
          <div className={p.list}>{filteredC.length > 0 ? filteredC.map(c => <MatchCard key={c.id} match={c} onItemClick={(x) => openItem(x, 'clasico')} />) : <div className={p.emptyState}><div className={p.emptyText}>No finished clasicos</div></div>}</div>
          <Pagination page={cPage} pages={cPages} total={cTotal} size={PAGE_SIZE} onPageChange={pg => { setCPage(pg); loadClasicos(pg); }} />
        </div>
      </div>

      <Modal open={!!selItem} onClose={() => setSelItem(null)} title={selType === 'clasico' ? 'Clasico Result' : 'Match Result'} wide>
        {selItem && <>
          {selType === 'clasico' && (
            <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={() => setWinOpen(true)} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', fontSize: '0.78rem', fontWeight: 600, borderRadius: 6, cursor: 'pointer', background: 'transparent', color: '#73c991', border: '1px solid rgba(115,201,145,0.4)' }}>
                <FiAward size={12} /> View Winning Markets
              </button>
            </div>
          )}
          <div className={p.scoreboard}>
            <div className={p.scoreTeam}><span className={p.scoreTeamName}>{home?.name ?? '—'}</span><span className={p.scoreTeamSide}>Home</span></div>
            <div style={{ textAlign: 'center' }}><div className={p.scoreMain}>{hFT != null && aFT != null ? `${hFT} : ${aFT}` : hFH != null && aFH != null ? `${hFH} : ${aFH}` : '— : —'}</div>{hFH != null && aFH != null && <div className={p.scoreHt}>HT: {hFH} : {aFH}</div>}</div>
            <div className={p.scoreTeam}><span className={p.scoreTeamName}>{away?.name ?? '—'}</span><span className={p.scoreTeamSide}>Away</span></div>
          </div>
          {[home, away].filter(Boolean).map(t => <div key={t.id} className={p.resultSection}><div className={p.resultLabel}>{getSideLabel(t)} — {t.name}</div><div style={{ display: 'flex', gap: 16, fontSize: '0.82rem', flexWrap: 'wrap' }}><span>Time 1st Goal: <strong>{(t.result?.timeOfFirstGoal != null && secsToTimeStr(t.result.timeOfFirstGoal)) || '—'}</strong></span><span>FH: {fmtInt(t.result?.firstHalfGoals)}</span><span>FT: {fmtInt(t.result?.fullTimeGoals)}</span></div></div>)}
          {/* Lambdas KPI strip */}
          {selType === 'clasico' && selItem.lambdas && (
            <div style={{ marginBottom: 16 }}>
              <h4 style={{ fontSize: '0.82rem', fontWeight: 600, marginBottom: 8, color: 'var(--text-secondary)' }}>Lambdas</h4>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))', gap: 6 }}>
                {LAMBDA_KPI_FIELDS.map(f => {
                  const v = selItem.lambdas[f.key];
                  return (
                    <div key={f.key} style={{ padding: '6px 8px', borderRadius: 6, background: 'var(--bg-secondary)', border: '1px solid var(--border-color)' }}>
                      <div style={{ fontSize: '0.62rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.4px' }}>{f.label}</div>
                      <div style={{ fontSize: '0.78rem', fontWeight: 600, color: v == null ? 'var(--text-muted)' : 'var(--text-primary)' }}>{v == null ? '—' : Number(v).toFixed(3)}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {/* Static quota sections — hide whole section when family/lambda missing or all values null; hide individual null fields. */}
          {QUOTA_SECTIONS.map(sec => {
            if (!familyAllowed(sec.label)) return null;
            const visible = sec.fields.filter(f => q[f.key] != null);
            if (!visible.length) return null;
            return (
              <div key={sec.label} style={{ marginBottom: 12 }}><h4 style={{ fontSize: '0.82rem', fontWeight: 600, marginBottom: 8, color: 'var(--text-secondary)' }}>{sec.label}</h4><div className={p.quotaGrid}>{visible.map(f => <div key={f.key} className={p.quotaField}><label className={p.quotaLabel}>{f.l}</label><div className={`${p.quotaValue} ${wins.has(f.key) ? p.quotaWin : ''}`}>{formatQuota(q[f.key])}</div></div>)}</div></div>
            );
          })}
          {/* Dynamic O/U sections — hide individual rows where over/under price missing. */}
          {dynamicOuSecs.map(sec => {
            if (!familyAllowed(sec.label)) return null;
            const visible = sec.fields.filter(f => q[f.key] != null);
            if (!visible.length) return null;
            return (
              <div key={sec.label} style={{ marginBottom: 12 }}><h4 style={{ fontSize: '0.82rem', fontWeight: 600, marginBottom: 8, color: 'var(--text-secondary)' }}>{sec.label}</h4><div className={p.quotaGrid}>{visible.map(f => <div key={f.key} className={p.quotaField}><label className={p.quotaLabel}>{f.l}</label><div className={p.quotaValue}>{formatQuota(q[f.key])}</div></div>)}</div></div>
            );
          })}
          {/* Team Totals */}
          {[home, away].filter(Boolean).map(t => (
            <div key={`tt-${t.id}`} style={{ marginBottom: 12 }}>
              <h4 style={{ fontSize: '0.82rem', fontWeight: 600, marginBottom: 8, color: 'var(--text-secondary)' }}>{getSideLabel(t)} — Team Totals</h4>
              <div className={p.quotaGrid}>
                {TT_FIELDS.filter(f => t[f.key] != null).map(f => <div key={f.key} className={p.quotaField}><label className={p.quotaLabel}>{f.l}</label><div className={p.quotaValue}>{formatQuota(t[f.key])}</div></div>)}
              </div>
              {/* Stat TT fields */}
              <div className={p.quotaGrid} style={{ marginTop: 8 }}>
                {STAT_TT_FIELDS.map(f => {
                  if (t[f.lineKey] == null) return null;
                  return (
                    <React.Fragment key={f.lineKey}>
                      <div className={p.quotaField}><label className={p.quotaLabel}>{f.l} {t[f.lineKey]}</label><div className={p.quotaValue}>Line: {formatQuota(t[f.lineKey])}</div></div>
                      <div className={p.quotaField}><label className={p.quotaLabel}>Over</label><div className={p.quotaValue}>{formatQuota(t[f.oKey])}</div></div>
                      <div className={p.quotaField}><label className={p.quotaLabel}>Under</label><div className={p.quotaValue}>{formatQuota(t[f.uKey])}</div></div>
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
          ))}
        </>}
      </Modal>

      <WinningMarketsModal
        open={winOpen}
        onClose={() => setWinOpen(false)}
        title={selItem ? `Winning Markets — Clasico #${selItem.id}` : 'Winning Markets'}
        fictiveMatchId={selType === 'clasico' ? selItem?.id : null}
      />
    </div>
  );
}

